import {useEffect, useState, useCallback} from 'react';
import {Keyboard, Platform, KeyboardEvent} from 'react-native';

interface KeyboardStatusOptions {
  /**
   * Optional callback that fires when keyboard shows
   */
  onKeyboardShow?: (event: KeyboardEvent) => void;
  /**
   * Optional callback that fires when keyboard hides
   */
  onKeyboardHide?: (event: KeyboardEvent) => void;
  /**
   * Include keyboard height in return value
   */
  includeHeight?: boolean;
}

/**
 * useKeyboardStatus is a custom hook to determine the visibility of the on-screen keyboard.
 * @param {KeyboardStatusOptions} options - Configuration options for the hook
 * @returns {Object} The keyboard state including visibility and optionally height
 */
const useKeyboardStatus = (options?: KeyboardStatusOptions) => {
  const [isKeyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const handleKeyboardShow = useCallback(
    (event: KeyboardEvent) => {
      // Get keyboard height from event
      const height = event.endCoordinates.height;
      setKeyboardHeight(height);
      setKeyboardVisible(true);
      options?.onKeyboardShow?.(event);
    },
    [options],
  );

  const handleKeyboardHide = useCallback(
    (event: KeyboardEvent) => {
      setKeyboardHeight(0);
      setKeyboardVisible(false);
      options?.onKeyboardHide?.(event);
    },
    [options],
  );

  useEffect(() => {
    // Different event names for iOS and Android to ensure consistency
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    // Add keyboard listeners
    const keyboardShowListener = Keyboard.addListener(
      showEvent,
      handleKeyboardShow,
    );
    
    const keyboardHideListener = Keyboard.addListener(
      hideEvent,
      handleKeyboardHide,
    );

    // Clean up listeners on unmount
    return () => {
      keyboardShowListener.remove();
      keyboardHideListener.remove();
    };
  }, [handleKeyboardShow, handleKeyboardHide]);

  return isKeyboardVisible;
};

export default useKeyboardStatus;
